import Api from 'services/api'

/**
 * Investor onboarding.
 *
 * Two audiences, two prefixes. `/investorOnboarding/*` is the applicant's own, addressed only by
 * the token in their link and never by a record id, so nothing here needs them to be signed in.
 * `/admin/investorOnboarding/*` is the staff queue and sits behind the fund permission.
 */
const PUBLIC = '/investorOnboarding'
const ADMIN = '/admin/investorOnboarding'

export type OnboardingStatus = 'invited' | 'inProgress' | 'submitted' | 'approved' | 'declined' | 'withdrawn'

export const ONBOARDING_STATUS_LABEL: Record<string, string> = {
  invited: 'Invited',
  inProgress: 'In Progress',
  submitted: 'Awaiting Review',
  approved: 'Approved',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
}

export type OnboardingStep = 'profile' | 'accreditation' | 'tax' | 'documents' | 'banking' | 'subscription'

export const ONBOARDING_STEP_LABEL: Record<string, string> = {
  profile: 'About you',
  accreditation: 'Accreditation',
  tax: 'Tax details',
  documents: 'Documents',
  banking: 'Banking',
  subscription: 'Your investment',
}

export interface OnboardingApplicantView {
  reference: string
  email: string
  fullName: string
  status: OnboardingStatus
  fund: { id: number; name: string; minimumInvestment: number } | null
  data: Record<string, any>
  completedSteps: string[]
  steps: OnboardingStep[]
  documents: { name: string; category: string; uploadedAt: number }[]
  signatures: { document: string; title: string; signedAt: number; signed: boolean }[]
  requiredSignatures: { document: string; title: string }[]
  /** Submitted, approved or declined: they can look, not change. */
  locked: boolean
  accreditationCriteria: Record<string, string>
  /**
   * Whether this tenant has a signing engine configured. Signing is required to submit, so `false`
   * means the application cannot be finished online at all, and the applicant is told up front.
   */
  eSignEnabled: boolean
}

export interface OnboardingListRow {
  id: number
  reference: string
  email: string
  fullName: string
  status: OnboardingStatus
  fundId: number
  fundName: string
  commitment: number
  stepsDone: number
  stepsTotal: number
  signatureCount: number
  participantUserId: number | null
  createdAt: string
  updatedAt: string
}

// ---- the applicant's side -----------------------------------------------------------------
// None of these take an id. The token is the only thing that identifies the application.

/**
 * Begin a self-serve application.
 *
 * Answers only with a message: the link is e-mailed, never returned, so posting somebody else's
 * address tells you nothing and reaches nothing.
 */
export const startInvestorOnboarding = (data: { email: string; fullName?: string; fundId?: number }) => {
  return Api.post(`${PUBLIC}/start`, data)
}

export const getInvestorOnboarding = (token: string): Promise<OnboardingApplicantView> => {
  return Api.get(`${PUBLIC}/:token`, {}, { token })
}

export const saveInvestorOnboardingStep = (
  token: string,
  step: OnboardingStep,
  payload: Record<string, any>,
): Promise<OnboardingApplicantView> => {
  return Api.put(`${PUBLIC}/:token/step/:step`, { payload }, { token, step })
}

export const uploadInvestorOnboardingDocument = (token: string, file: File, category: string) => {
  return Api.uploadFiles(`${PUBLIC}/:token/documents`, { category }, { token }, [file])
}

export const removeInvestorOnboardingDocument = (token: string, name: string) => {
  return Api.delete(`${PUBLIC}/:token/documents/:name`, {}, { token, name: encodeURIComponent(name) })
}

/** The document as it currently reads, so the applicant can check it before signing. */
export const previewInvestorOnboardingDocument = (
  token: string,
  document: string,
): Promise<{ document: string; title: string; pdf: string }> => {
  return Api.get(`${PUBLIC}/:token/documents/:document/preview`, {}, { token, document })
}

export const signInvestorOnboardingDocument = (token: string, document: string) => {
  return Api.post(`${PUBLIC}/:token/documents/:document/sign`, {}, { token, document })
}

export const submitInvestorOnboarding = (token: string) => {
  return Api.post(`${PUBLIC}/:token/submit`, {}, { token })
}

// ---- the staff side -------------------------------------------------------------------------

export const filterInvestorOnboarding = (
  filters: Record<string, any>,
): Promise<{ total: number; data: OnboardingListRow[] }> => {
  return Api.get(ADMIN, filters)
}

export const inviteInvestorOnboarding = (data: {
  email: string
  fullName?: string
  fundId?: number
  participantUserId?: number
}) => {
  return Api.post(`${ADMIN}/invite`, data)
}

export const getInvestorOnboardingDetail = (id: number): Promise<Record<string, any>> => {
  return Api.get(`${ADMIN}/:id`, {}, { id })
}

export const downloadInvestorOnboardingDocument = (id: number, key: string): Promise<{ url: string }> => {
  return Api.post(`${ADMIN}/:id/document`, { key }, { id })
}

export const resendInvestorOnboardingInvite = (id: number) => {
  return Api.post(`${ADMIN}/:id/resend`, {}, { id })
}

export const approveInvestorOnboarding = (
  id: number,
  data: { commitment?: number; openingAmount?: number; note?: string },
) => {
  return Api.post(`${ADMIN}/:id/approve`, data, { id })
}

export const declineInvestorOnboarding = (id: number, data: { note?: string }) => {
  return Api.post(`${ADMIN}/:id/decline`, data, { id })
}

/** Send it back for the applicant to fix, which re-opens their link. */
export const reopenInvestorOnboarding = (id: number, data: { note?: string }) => {
  return Api.post(`${ADMIN}/:id/reopen`, data, { id })
}
